import type { Schema } from 'type-fest';
import {
  array,
  object,
  string,
  mixed,
  number,
  ValidationError,
  type TestContext,
} from 'yup';

import {
  type Rule,
  type Resolution,
  RESOLUTION_PARAMETER_TYPES,
  ResolutionConfigItemType,
  ResolutionConfigItem,
  ResolutionParameter,
  ResolutionConfig,
  ResolutionConfigMap,
  FilterValueType,
  type RangeFilterValue,
  COUNT_OPERATORS,
  STRING_OPERATORS,
  type CountOperator,
  REQS_REVIEW_RESOLUTION_TYPES,
  OPERATORS,
  NUMERIC_ONLY_OPERATORS,
  FILTER_QUERY_VALUE_TYPES,
  FilterQueryValueType,
  REQS_REVIEW_CONFIG_ITEM_FILTER_TYPES,
  FILTER_UNITS,
  SELECT_OPERATORS,
  NULL_OPERATORS,
} from '@frontend/api';

import messages from 'constants/messages';

import type { FormStateData } from '../components/RulesDetailModal';

export const isRangeValue = (
  value: FilterValueType,
): value is RangeFilterValue =>
  typeof value === 'object' &&
  value !== null &&
  'min' in value &&
  'max' in value;

const isListOfRanges = (
  val: unknown,
): val is Array<{ min: number; max: number }> => {
  return (
    Array.isArray(val) &&
    val.length > 0 &&
    val.every((item) => isRangeValue(item))
  );
};

export const isRangeOperator = (operator: CountOperator) =>
  operator === COUNT_OPERATORS.RANGE;

export const isNullableResolutionParameter = (
  resolutionType: ResolutionConfigItemType,
) =>
  resolutionType === REQS_REVIEW_RESOLUTION_TYPES.NO_QUOTE ||
  resolutionType === REQS_REVIEW_RESOLUTION_TYPES.RESOLVE ||
  resolutionType === REQS_REVIEW_RESOLUTION_TYPES.START_CHAT;

type ValidationResult = {
  isValid: boolean;
  errorMessage?: string;
  validatedValue?: FilterValueType;
};

// Maps for filter type to allowed operators and value types
const FILTER_TYPE_TO_OPERATOR_MAP = {
  [REQS_REVIEW_CONFIG_ITEM_FILTER_TYPES.NUMERIC]: [
    OPERATORS.EQUALS,
    OPERATORS.GREATER_THAN,
    OPERATORS.GREATER_THAN_OR_EQUAL,
    OPERATORS.LESS_THAN,
    OPERATORS.LESS_THAN_OR_EQUAL,
    OPERATORS.RANGE,
  ],
  [REQS_REVIEW_CONFIG_ITEM_FILTER_TYPES.STRING]: [
    OPERATORS.INCLUDES,
    OPERATORS.INCLUDES_CASE_INSENSITIVE,
    OPERATORS.REGEX,
  ],
  [REQS_REVIEW_CONFIG_ITEM_FILTER_TYPES.SELECT]: [
    OPERATORS.IS_IN,
    OPERATORS.NOT_IN,
    OPERATORS.EQUALS,
    OPERATORS.IS_NULL,
  ],
  [REQS_REVIEW_CONFIG_ITEM_FILTER_TYPES.BOOLEAN]: [OPERATORS.EQUALS],
};

const FILTER_TYPE_TO_VALUE_TYPE_MAP = {
  [REQS_REVIEW_CONFIG_ITEM_FILTER_TYPES.NUMERIC]: [
    FILTER_QUERY_VALUE_TYPES.NUMBER,
    FILTER_QUERY_VALUE_TYPES.DISTANCE,
    FILTER_QUERY_VALUE_TYPES.ANGLE,
    FILTER_QUERY_VALUE_TYPES.AREA,
    FILTER_QUERY_VALUE_TYPES.VOLUME,
  ],
  [REQS_REVIEW_CONFIG_ITEM_FILTER_TYPES.STRING]: [
    FILTER_QUERY_VALUE_TYPES.STRING,
  ],
  [REQS_REVIEW_CONFIG_ITEM_FILTER_TYPES.SELECT]: [
    FILTER_QUERY_VALUE_TYPES.STRING,
    FILTER_QUERY_VALUE_TYPES.NUMBER,
  ],
  [REQS_REVIEW_CONFIG_ITEM_FILTER_TYPES.BOOLEAN]: [
    FILTER_QUERY_VALUE_TYPES.BOOLEAN,
  ],
};

const VALUE_TYPE_TO_AVAILABLE_UNITS = {
  [FILTER_QUERY_VALUE_TYPES.DISTANCE]: [FILTER_UNITS.IN, FILTER_UNITS.MM],
  [FILTER_QUERY_VALUE_TYPES.AREA]: [FILTER_UNITS.IN, FILTER_UNITS.MM],
  [FILTER_QUERY_VALUE_TYPES.VOLUME]: [FILTER_UNITS.IN, FILTER_UNITS.MM],
  [FILTER_QUERY_VALUE_TYPES.ANGLE]: [FILTER_UNITS.DEG],
  [FILTER_QUERY_VALUE_TYPES.STRING]: [],
  [FILTER_QUERY_VALUE_TYPES.NUMBER]: [],
  [FILTER_QUERY_VALUE_TYPES.BOOLEAN]: [],
};

/**
 * Validate that an operator is compatible with both value type and filter type.
 *
 * @param operator - The operator string to validate
 * @param valueType - The value type string to validate against
 * @param filterType - The filter type string to validate against
 * @returns ValidationResult
 */
export function validateOperatorValueTypeAndFilterTypeCompatible(
  operator: string,
  valueType: string,
  filterType: string,
): ValidationResult {
  // Validate filterType
  if (!(filterType in FILTER_TYPE_TO_OPERATOR_MAP)) {
    return {
      isValid: false,
      errorMessage: `Filter type '${filterType}' is not supported. Supported filter types are: ${Object.keys(
        FILTER_TYPE_TO_OPERATOR_MAP,
      ).join(', ')}. Please use one of these supported filter types.`,
    };
  }

  // Validate valueType
  if (!FILTER_TYPE_TO_VALUE_TYPE_MAP[filterType].includes(valueType)) {
    return {
      isValid: false,
      errorMessage: `Value type '${valueType}' is not supported for filter type '${filterType}'. Supported value types are: ${FILTER_TYPE_TO_VALUE_TYPE_MAP[
        filterType
      ].join(', ')}. Please use one of these supported value types.`,
    };
  }

  // Validate operator
  if (!FILTER_TYPE_TO_OPERATOR_MAP[filterType].includes(operator)) {
    return {
      isValid: false,
      errorMessage: `Operator '${operator}' is not supported for filter type '${filterType}'. Supported operators are: ${FILTER_TYPE_TO_OPERATOR_MAP[
        filterType
      ].join(', ')}. Please use one of these supported operators.`,
    };
  }

  return { isValid: true };
}

/**
 * Validate that units are compatible with a given value type.
 *
 * @param units - The units string to validate
 * @param valueType - The value type string to validate against
 * @returns ValidationResult
 */
export function validateUnitsCompatibleWithValueType(
  units: string,
  valueType: string,
): ValidationResult {
  if (!(valueType in VALUE_TYPE_TO_AVAILABLE_UNITS)) {
    return {
      isValid: false,
      errorMessage: `Value type '${valueType}' is not supported for unit validation. Supported value types are: ${Object.keys(
        VALUE_TYPE_TO_AVAILABLE_UNITS,
      ).join(', ')}. Please use one of these supported value types.`,
    };
  }

  const validUnits = VALUE_TYPE_TO_AVAILABLE_UNITS[valueType];
  if (units && !validUnits.includes(units)) {
    return {
      isValid: false,
      errorMessage: `Units '${units}' are not compatible with value type '${valueType}'. For value type '${valueType}', you can only use these units: ${validUnits.join(
        ', ',
      )}. Please choose compatible units for your value type.`,
    };
  }

  return { isValid: true };
}

/**
 * Coerce a value to int or float, returning validation status and result.
 *
 * @param value - The value to coerce
 * @returns ValidationResult object
 */
const coerceToIntOrFloat = (value: FilterValueType): ValidationResult => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return { isValid: true, validatedValue: value };
  } else if (typeof value === 'string') {
    // Try to parse as int
    if (/^-?\d+$/.test(value.trim())) {
      const intVal = parseInt(value, 10);
      return { isValid: true, validatedValue: intVal };
    }
    // Try to parse as float
    if (
      /^-?\d*\.\d+$/.test(value.trim()) ||
      /^-?\d+\.\d*$/.test(value.trim())
    ) {
      const floatVal = parseFloat(value);
      return { isValid: true, validatedValue: floatVal };
    }
    // Try parseFloat as fallback
    const floatVal = parseFloat(value);
    if (!isNaN(floatVal)) {
      return { isValid: true, validatedValue: floatVal };
    }
    return {
      isValid: false,
      errorMessage: `Unable to convert string '${value}' to a number. The string must contain only digits and optionally a decimal point (e.g., '123', '45.67', '-12.34'). Please provide a valid numeric string.`,
      validatedValue: value,
    };
  } else {
    return {
      isValid: false,
      errorMessage: `Value of type '${typeof value}' cannot be converted to a number. Only integers, floats, and numeric strings are supported. Please provide a numeric value.`,
      validatedValue: value,
    };
  }
};

/**
 * Validate that a value is compatible with a given value type and coerce it appropriately.
 *
 * @param value - The value to validate and coerce
 * @param valueType - The value type string to validate against
 * @returns ValidationResult object
 */
export const validateValueCompatibleWithValueType = (
  value: FilterValueType,
  valueType: FilterQueryValueType,
): ValidationResult => {
  // Use the FilterValueType "enum" object from @frontend/api
  const validValueTypes = Object.values(FILTER_QUERY_VALUE_TYPES);

  if (!validValueTypes.includes(valueType)) {
    return {
      isValid: false,
      errorMessage: `Invalid value type '${valueType}'. Valid value types are: ${validValueTypes.join(
        ', ',
      )}. Please use one of these supported value types for your filter.`,
      validatedValue: value,
    };
  }

  // Helper checks
  const isList = Array.isArray(value);
  const isRange = isRangeValue(value);
  const isValListOfRanges = isListOfRanges(value);

  if (valueType === FILTER_QUERY_VALUE_TYPES.STRING) {
    if (isValListOfRanges) {
      return {
        isValid: false,
        errorMessage:
          'List of range values are not valid for string type filters. String filters only support single strings, lists of strings, or individual string values. Please provide a string value or list of strings instead of ranges.',
        validatedValue: value,
      };
    } else if (isRange) {
      return {
        isValid: false,
        errorMessage:
          'Range values are not valid for string type filters. String filters only support single strings, lists of strings, or individual string values. Please provide a string value instead of a range.',
        validatedValue: value,
      };
    } else if (isList) {
      try {
        const coerced = (value as unknown[]).map((item) => String(item));
        return { isValid: true, validatedValue: coerced };
      } catch {
        return {
          isValid: false,
          errorMessage:
            'Unable to convert all items in the list to strings. For string type filters, all list items must be convertible to strings. Please ensure all items are string-compatible.',
          validatedValue: value,
        };
      }
    } else {
      try {
        return { isValid: true, validatedValue: String(value) };
      } catch {
        return {
          isValid: false,
          errorMessage: `Unable to convert value '${value}' to a string. For string type filters, the value must be convertible to a string. Please provide a string-compatible value.`,
          validatedValue: value,
        };
      }
    }
  } else if (
    valueType === FILTER_QUERY_VALUE_TYPES.NUMBER ||
    valueType === FILTER_QUERY_VALUE_TYPES.DISTANCE ||
    valueType === FILTER_QUERY_VALUE_TYPES.AREA ||
    valueType === FILTER_QUERY_VALUE_TYPES.VOLUME ||
    valueType === FILTER_QUERY_VALUE_TYPES.ANGLE
  ) {
    if (isValListOfRanges) {
      const coercedRanges: { min: number; max: number }[] = [];
      for (let i = 0; i < (value as unknown[]).length; i++) {
        const item = (value as unknown[])[i];
        if (
          typeof item !== 'object' ||
          item === null ||
          !('min' in item) ||
          !('max' in item)
        ) {
          return {
            isValid: false,
            errorMessage: `Item ${i} in the list is not a valid range object. Each item must be an object with 'min' and 'max' keys. Found: ${JSON.stringify(
              item,
            )}`,
            validatedValue: value,
          };
        }
        const minResult = coerceToIntOrFloat(item.min);
        if (!minResult.isValid) {
          return {
            isValid: false,
            errorMessage: `Invalid min value in range ${i}: ${minResult.errorMessage}`,
            validatedValue: value,
          };
        }
        const maxResult = coerceToIntOrFloat(item.max);
        if (!maxResult.isValid) {
          return {
            isValid: false,
            errorMessage: `Invalid max value in range ${i}: ${maxResult.errorMessage}`,
            validatedValue: value,
          };
        }
        coercedRanges.push({
          min: minResult.validatedValue as number,
          max: maxResult.validatedValue as number,
        });
      }
      return { isValid: true, validatedValue: coercedRanges };
    } else if (isRange) {
      if (
        typeof value !== 'object' ||
        value === null ||
        !('min' in value) ||
        !('max' in value)
      ) {
        return {
          isValid: false,
          errorMessage: `Range value must be an object with 'min' and 'max' keys. Found: ${JSON.stringify(
            value,
          )}. Please provide an object with both 'min' and 'max' values.`,
          validatedValue: value,
        };
      }
      const minResult = coerceToIntOrFloat(value.min);
      if (!minResult.isValid) {
        return {
          isValid: false,
          errorMessage: `Invalid min value in range: ${minResult.errorMessage}`,
          validatedValue: value,
        };
      }
      const maxResult = coerceToIntOrFloat(value.max);
      if (!maxResult.isValid) {
        return {
          isValid: false,
          errorMessage: `Invalid max value in range: ${maxResult.errorMessage}`,
          validatedValue: value,
        };
      }
      return {
        isValid: true,
        validatedValue: {
          min: minResult.validatedValue,
          max: maxResult.validatedValue,
        },
      };
    } else if (isList) {
      const coercedList: number[] = [];
      for (let i = 0; i < (value as unknown[]).length; i++) {
        const itemResult = coerceToIntOrFloat(value[i]);
        if (!itemResult.isValid) {
          return {
            isValid: false,
            errorMessage: `Invalid value at index ${i}: ${itemResult.errorMessage}`,
            validatedValue: value,
          };
        }
        coercedList.push(itemResult.validatedValue as number);
      }
      return { isValid: true, validatedValue: coercedList };
    } else {
      const itemResult = coerceToIntOrFloat(value);
      if (!itemResult.isValid) {
        return {
          isValid: false,
          errorMessage: itemResult.errorMessage,
          validatedValue: value,
        };
      }
      return { isValid: true, validatedValue: itemResult.validatedValue };
    }
  } else if (valueType === FILTER_QUERY_VALUE_TYPES.BOOLEAN) {
    return { isValid: true, validatedValue: Boolean(value) };
  } else {
    return {
      isValid: false,
      errorMessage: `Invalid value type '${valueType}'. Valid value types are: ${validValueTypes.join(
        ', ',
      )}. Please use one of these supported value types.`,
      validatedValue: value,
    };
  }
};

/**
 * Validate that a value is compatible with a given operator.
 *
 * @param value - The value to validate
 * @param operator - The operator string to validate against
 * @returns ValidationResult object
 */
export const validateValueCompatibleWithOperator = (
  value: FilterValueType,
  operator: string,
): ValidationResult => {
  const isList = Array.isArray(value);

  // Validate operator
  if (!Object.values(OPERATORS).includes(operator as CountOperator)) {
    return {
      isValid: false,
      errorMessage: `Invalid operator '${operator}'. Valid operators are: ${Object.values(
        OPERATORS,
      ).join(
        ', ',
      )}. Please use one of these supported operators for your filter.`,
      validatedValue: value,
    };
  }

  // Validation logic
  if (operator === COUNT_OPERATORS.RANGE) {
    if (!isRangeValue(value)) {
      return {
        isValid: false,
        errorMessage: `Operator '${operator}' requires an object with 'min' and 'max' keys. Found: ${typeof value}. Please provide an object like { min: 10, max: 20 } for range operations.`,
        validatedValue: value,
      };
    } else if (isRangeValue(value) && value.min > value.max) {
      return {
        isValid: false,
        errorMessage: `Range min value (${value.min}) must be less than or equal to max value (${value.max}). For range operations, the minimum value should always be smaller than or equal to the maximum value. Please correct the range values.`,
        validatedValue: value,
      };
    }
  } else if (operator !== COUNT_OPERATORS.RANGE && isRangeValue(value)) {
    return {
      isValid: false,
      errorMessage: `Operator '${operator}' does not support range values.`,
      validatedValue: value,
    };
  } else if (
    operator === NUMERIC_ONLY_OPERATORS.ON_INTERVAL ||
    operator === NUMERIC_ONLY_OPERATORS.NOT_ON_INTERVAL
  ) {
    if (!isListOfRanges(value)) {
      return {
        isValid: false,
        errorMessage: `Operator '${operator}' requires a list of objects with 'min' and 'max' keys. Found: ${typeof value}. Please provide a list of ranges like [{ min: 10, max: 20 }, { min: 30, max: 40 }] for interval operations.`,
        validatedValue: value,
      };
    }
    for (let i = 0; i < value.length; i++) {
      const item = value[i];
      if (item.min > item.max) {
        return {
          isValid: false,
          errorMessage: `Range ${i} has min value (${item.min}) greater than max value (${item.max}). For interval operations, each range's minimum value should be smaller than or equal to its maximum value. Please correct the range values in the list.`,
          validatedValue: value,
        };
      }
    }
  } else if (
    operator === SELECT_OPERATORS.IS_IN ||
    operator === SELECT_OPERATORS.NOT_IN ||
    operator === STRING_OPERATORS.INCLUDES ||
    operator === STRING_OPERATORS.INCLUDES_CASE_INSENSITIVE
  ) {
    if (!isList) {
      return {
        isValid: false,
        errorMessage: `Operator '${operator}' requires a list of values. Found: ${typeof value}. Please provide a list of values like ['value1', 'value2', 'value3'] or [1, 2, 3] for membership operations.`,
        validatedValue: value,
      };
    }
  } else if (operator === STRING_OPERATORS.REGEX) {
    // Only validate that the value is non-null/non-empty
    // The general validation below will handle null/empty checks
    return { isValid: true, validatedValue: value };
  } else if (operator === NULL_OPERATORS.IS_NULL) {
    // IS_NULL operator expects boolean value (true = is null, false = is not null)
    return { isValid: true, validatedValue: value };
  }

  return { isValid: true, validatedValue: value };
};

type QueryValueValidationResult = {
  isValid: boolean;
  errorMessage?: string;
};

/**
 * Validates a query value against its operator, value type, filter type, and units.
 *
 * @param value - The value to validate
 * @param operator - The operator string
 * @param valueType - The value type string
 * @param filterType - The filter type string
 * @param units - The units string (optional)
 * @returns QueryValueValidationResult
 */
const validateQueryValue = (
  value: FilterValueType,
  operator: string,
  valueType: FilterQueryValueType,
  filterType: string,
  units?: string,
): QueryValueValidationResult => {
  const {
    isValid: isValidOperatorValueTypeAndFilterType,
    errorMessage: errorMessageOperatorValueTypeAndFilterType,
  } = validateOperatorValueTypeAndFilterTypeCompatible(
    operator,
    valueType,
    filterType,
  );
  if (!isValidOperatorValueTypeAndFilterType) {
    return {
      isValid: false,
      errorMessage: errorMessageOperatorValueTypeAndFilterType,
    };
  }

  if (units) {
    const { isValid: isValidUnits, errorMessage: errorMessageUnits } =
      validateUnitsCompatibleWithValueType(units, valueType);
    if (!isValidUnits) {
      return {
        isValid: false,
        errorMessage: errorMessageUnits,
      };
    }
  }

  // Skip value type check for IS_NULL operator since it uses a placeholder value
  let validatedValueFromValueTypeCheck: FilterValueType | undefined = value;
  if (operator !== NULL_OPERATORS.IS_NULL) {
    const {
      isValid: isValidValueType,
      errorMessage: errorMessageValueType,
      validatedValue,
    } = validateValueCompatibleWithValueType(value, valueType);
    if (!isValidValueType) {
      return {
        isValid: false,
        errorMessage: errorMessageValueType,
      };
    }
    validatedValueFromValueTypeCheck = validatedValue;
  }

  const { isValid: isValidOperator, errorMessage: errorMessageOperator } =
    validateValueCompatibleWithOperator(
      validatedValueFromValueTypeCheck ?? value,
      operator,
    );
  if (!isValidOperator) {
    return {
      isValid: false,
      errorMessage: errorMessageOperator,
    };
  }

  // after above validations, just need to do some cursory checks
  if (value === null || value === '' || value === undefined) {
    return {
      isValid: false,
      errorMessage: 'Value cannot be empty',
    };
  } else if (Array.isArray(value)) {
    if (value.length === 0) {
      return {
        isValid: false,
        errorMessage: 'Array cannot be empty',
      };
    }
  }

  return { isValid: true };
};

// We need to define these custom types for validation at this specific level because
// the values are dynamic to the level of the schema
type ResolutionParameterValidationFromArray = [
  {
    schema: Schema<FormStateData, ResolutionParameter>;
    value: ResolutionParameter;
  },
  {
    schema: Schema<FormStateData, Resolution>;
    value: Resolution;
  },
  {
    schema: Schema<FormStateData, Rule>;
    value: Rule;
  },
];
type ResolutionParameterValidationContextOptions = {
  index: number;
  context: {
    resolutionConfig?: ResolutionConfig & { mapped: ResolutionConfigMap };
  };
};
type ResolutionParameterValidationContext = Omit<
  TestContext<ResolutionParameterValidationContextOptions>,
  'options'
> & {
  options: ResolutionParameterValidationContextOptions;
  from: ResolutionParameterValidationFromArray;
};

const RESOLUTION_PARAMETER_SCHEMA = object().shape({
  name: string().required(),
  value: mixed().test('parameter-type', (value, context) => {
    const { options, from, createError, path } =
      context as ResolutionParameterValidationContext;
    const {
      context: { resolutionConfig },
      index,
    } = options;
    if (!resolutionConfig) return true;
    // Pull out the parent object from the "from" and get the necessary parameterType to validate against
    const { value: resolution } = from[1];
    const type: ResolutionConfigItemType = resolution.type;
    const configItem: ResolutionConfigItem = resolutionConfig.mapped[type];
    const parameterType = configItem.requiredParameters[index].parameterType;

    // Based on the parameterType, validate the value against the appropriate schema
    switch (parameterType) {
      case RESOLUTION_PARAMETER_TYPES.STRING:
        if (!value) return createError({ message: messages.REQUIRED, path });
        return true;
      case RESOLUTION_PARAMETER_TYPES.NUMERIC:
        if (!value || value < 1)
          return createError({ message: messages.REQUIRED, path });
        return true;
      case RESOLUTION_PARAMETER_TYPES.NUMERIC_LIST:
        if (!Array.isArray(value) || value.length === 0)
          return createError({ message: messages.REQUIRED, path });
        return true;
      case RESOLUTION_PARAMETER_TYPES.BOOLEAN:
        if (typeof value !== 'boolean')
          return createError({ message: messages.REQUIRED, path });
        return true;
    }
  }),
});

const FORM_VALIDATION_SCHEMA = object().shape({
  name: string().required(messages.REQUIRED),
  description: string(),
  signals: array()
    .of(
      object().shape({
        groups: array().of(
          object().shape({
            documentPath: string().required(),
            queries: array().of(
              object().shape({
                fieldName: array().of(string()).required(),
                operator: string().required(),
                valueType: string().required(),
                filterType: string().required(),
                units: string().nullable(),
                value: mixed()
                  .test('query-value', (value, context) => {
                    const { operator, valueType, filterType, units } =
                      context.parent;
                    const { createError } = context;

                    const validationResult = validateQueryValue(
                      value,
                      operator,
                      valueType,
                      filterType,
                      units,
                    );
                    if (!validationResult.isValid) {
                      return createError({
                        message:
                          validationResult.errorMessage ??
                          'Invalid query value',
                        path: context.path,
                      });
                    }

                    return true;
                  })
                  .required(),
              }),
            ),
            countQuery: mixed()
              .nullable()
              .test('count-query-validation', (value, context) => {
                // If countQuery is null or undefined, it's valid (optional)
                if (value === null || value === undefined) {
                  return true;
                }

                // If countQuery exists, it must be an object with operator and value
                if (typeof value !== 'object' || value === null) {
                  return context.createError({
                    message:
                      'Count query must be an object with operator and value',
                    path: context.path,
                  });
                }

                const countQuery = value as any;

                // Validate operator is present and required
                if (!countQuery.operator) {
                  return context.createError({
                    message: 'Count query operator is required',
                    path: `${context.path}.operator`,
                  });
                }

                // Validate value using the same logic as filter queries
                const validationResult = validateQueryValue(
                  countQuery.value,
                  countQuery.operator,
                  FILTER_QUERY_VALUE_TYPES.NUMBER,
                  REQS_REVIEW_CONFIG_ITEM_FILTER_TYPES.NUMERIC,
                );
                if (!validationResult.isValid) {
                  return context.createError({
                    message:
                      validationResult.errorMessage ??
                      'Invalid count query value',
                    path: `${context.path}.value`,
                  });
                }

                return true;
              }),
          }),
        ),
      }),
    )
    .min(1, messages.REQUIRED),
  resolutions: array()
    .of(
      object({
        type: string().required(messages.REQUIRED),
        customLabel: string().when('type', {
          is: 'RESOLVE',
          then: () => string().required('This field is required'),
          otherwise: () => string().nullable(),
        }),
        parameters: array().of(RESOLUTION_PARAMETER_SCHEMA),
      }).shape({
        parameters: array().when('type', {
          is: isNullableResolutionParameter,
          then: () =>
            array().of(
              object().shape({
                name: string().required(),
                value: mixed().nullable(),
              }),
            ),
          otherwise: () => array().of(RESOLUTION_PARAMETER_SCHEMA),
        }),
      }),
    )
    .min(1, messages.REQUIRED),
  defaultAssigneeId: number().nullable(),
});

// Create memoized validation function so we can map resolution parameters to their respective validation schemas
export const validateRule = (
  values: FormStateData,
  resolutionConfig: ResolutionConfig & { mapped: ResolutionConfigMap },
) => {
  try {
    FORM_VALIDATION_SCHEMA.validateSync(values, {
      context: { resolutionConfig },
      // Collect all errors, not just the first one
      abortEarly: false,
    });
    return undefined;
  } catch (error) {
    // Map errors to expected format
    if (error instanceof ValidationError) {
      return error.inner.reduce<Record<string, string>>(
        (acc, err) => ({ ...acc, [err.path ?? '']: err.errors[0] }),
        {},
      );
    }
  }
};
